import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

import { login as apiLogin, logout as apiLogout, verifyToken } from './api';

export type AuthContextType = {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SOCIAL_AUTH_ERROR_MESSAGE = 'Social login failed. Please try again.';
const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  oauth_not_configured: 'This login method is not configured.',
  google_workspace_domain_not_allowed: 'Use your weblium.com Google Workspace account to sign in.',
  google_workspace_email_missing: 'Google did not return an email address for this account.',
  google_workspace_group_required: 'Your Weblium Google account is not allowed for this environment. Ask an administrator to add it to the required access group.',
  google_workspace_group_check_failed: 'We could not verify your Google Workspace group access. Please try again or contact an administrator.',
};

const getOAuthErrorMessage = (errorCode: string | null, errorMessage: string | null): string => {
  if (errorMessage) {
    return errorMessage;
  }

  if (errorCode && OAUTH_ERROR_MESSAGES[errorCode]) {
    return OAUTH_ERROR_MESSAGES[errorCode];
  }

  return SOCIAL_AUTH_ERROR_MESSAGE;
};

const readOAuthRedirectParams = (): URLSearchParams | null => {
  const hashParams = new URLSearchParams(window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '');

  if (hashParams.has('authToken') || hashParams.has('authError')) {
    return hashParams;
  }

  const queryParams = new URLSearchParams(window.location.search);
  if (queryParams.has('authToken') || queryParams.has('authError')) {
    return queryParams;
  }

  return null;
};

const clearOAuthRedirectParams = () => {
  const url = new URL(window.location.href);

  url.searchParams.delete('authToken');
  url.searchParams.delete('authError');
  url.searchParams.delete('authErrorMessage');
  url.searchParams.delete('authProvider');
  url.hash = '';

  window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkToken = async () => {
      const oauthRedirectParams = readOAuthRedirectParams();

      if (oauthRedirectParams) {
        const oauthToken = oauthRedirectParams.get('authToken');
        const oauthError = oauthRedirectParams.get('authError');
        const oauthErrorMessage = oauthRedirectParams.get('authErrorMessage');

        clearOAuthRedirectParams();

        if (oauthError || !oauthToken) {
          localStorage.removeItem('authToken');
          sessionStorage.setItem('authError', getOAuthErrorMessage(oauthError, oauthErrorMessage));
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        const validOAuthToken = await verifyToken(oauthToken);
        if (validOAuthToken) {
          localStorage.setItem('authToken', oauthToken);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('authToken');
          sessionStorage.setItem('authError', SOCIAL_AUTH_ERROR_MESSAGE);
          setIsAuthenticated(false);
        }

        setLoading(false);
        return;
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      const valid = await verifyToken(token);
      setIsAuthenticated(valid);
      if (!valid) {
        localStorage.removeItem('authToken');
      }
      setLoading(false);
    };
    checkToken();
  }, []);

  const handleLogin = async (username: string, password: string) => {
    setLoading(true);
    try {
      const token = await apiLogin(username, password);
      localStorage.setItem('authToken', token);
      setIsAuthenticated(true);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const handleLogout = () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      void apiLogout(token).catch(error => console.warn('Unable to notify websocket sessions about logout', error));
    }
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      login: handleLogin,
      logout: handleLogout,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
